import React, { useCallback, useState } from 'react'
import { Text } from '@hub/text'
import useForm, {
  IFormErrors,
  IFormValues,
} from '@scentregroup/shared/hooks/use-form'
import { Form, FormInput } from '@scentregroup/shared/hub-components/form'
import { useSafeMutation } from '../../lib/apollo-client'
import { UnsubscribeCustomerWithEmailDocument } from '@scentregroup/shared/graphql'
import { useRecoilValue } from 'recoil'
import { userAtom } from '../../lib/atoms/user-info'
import { useRouter } from 'next/router'
import { useLocalUser } from '@scentregroup/shared/hooks/use-local-user'
import { validateEmail } from '../../lib/form'
import { onError } from '../../lib/on-error'
import useNotification from '../../lib/use-notification'
import { ERROR_UNSUBSCRIBE_WITH_EMAIL } from '../../lib/error-messages'
import { H3 } from '@hub/heading'
import { Stack } from '@hub/stack'
import { Button, ButtonStack } from '../button'
import { UNSUBSCRIBE_COMPLETE } from '../../lib/paths'

type Email = {
  email: string
}

type unsubscribeEmailErrors = IFormErrors<Email>
type unsubscribeEmailValues = IFormValues<Email>

enum UnsubscribeFields {
  EMAIL = 'email',
}

const UnsubscribeDirect: React.FC<React.PropsWithChildren<unknown>> = () => {
  const CHANNEL_UNSUBSCRIBE_FORM = 'unsubscribe-form'
  const { openNotification } = useNotification()
  const [unsubscribeCustomerWithEmail] = useSafeMutation(
    UnsubscribeCustomerWithEmailDocument
  )
  const user = useLocalUser()
  const { refetch } = useRecoilValue(userAtom)
  const [isSaving, setIsSaving] = useState(false)
  const router = useRouter()

  const defaultUnsubscribeEmail = { email: user?.email }

  const validateUnsubscribeEmail = (
    values: unsubscribeEmailValues
  ): unsubscribeEmailErrors => {
    const errors: unsubscribeEmailErrors = {}
    errors[UnsubscribeFields.EMAIL] = validateEmail(values.email)

    return errors
  }

  const onSave = useCallback(
    async (values: Email) => {
      setIsSaving(true)

      try {
        await unsubscribeCustomerWithEmail({
          variables: {
            input: {
              channel: CHANNEL_UNSUBSCRIBE_FORM,
              email: values.email,
            },
          },
        })

        if (refetch) {
          await refetch()
        }

        await router.push(`/${UNSUBSCRIBE_COMPLETE}`)
      } catch (error) {
        onError(error, openNotification, ERROR_UNSUBSCRIBE_WITH_EMAIL)
      } finally {
        setIsSaving(false)
      }
    },
    [router, refetch, unsubscribeCustomerWithEmail, openNotification]
  )

  const {
    errors,
    values,
    resetValues,
    clearErrors,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useForm<Email>(validateUnsubscribeEmail, onSave, defaultUnsubscribeEmail)

  const onCancel = useCallback(async () => {
    resetValues([UnsubscribeFields.EMAIL])
    clearErrors()

    if (user?.isLoggedIn) {
      router.push('/')
    } else {
      window.location.replace('/')
    }
  }, [user, router, resetValues, clearErrors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    clearErrors()
    handleChange({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })
  }

  const onBlur = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void =>
    handleBlur({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })

  return (
    <>
      <H3 sx={{ marginBottom: 'spacing-xl' }}>Newsletter unsubscribe</H3>
      <Text isBodyText={true} sx={{ marginBottom: 'spacing-xl' }}>
        {`We're sorry to see you go.`}
      </Text>
      <Form onSubmit={handleSubmit} gap={'spacing-lg'}>
        <Stack>
          <FormInput
            id={'email'}
            name={UnsubscribeFields.EMAIL}
            label={'Email address'}
            value={values.email || ''}
            type={'email'}
            inputMode={'email'}
            autoComplete={'email'}
            autoCorrect={'off'}
            autoCapitalize={'off'}
            isInvalid={!!errors.email}
            errorMessage={errors.email}
            isLoading={false}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Stack>
        <ButtonStack>
          <Button type={'submit'} isFullWidth isLoading={isSaving}>
            Save
          </Button>
          <Button variant={'outline'} isFullWidth onClick={onCancel}>
            Cancel
          </Button>
        </ButtonStack>
      </Form>
    </>
  )
}

export default UnsubscribeDirect
